import {
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Switch,
} from 'platform/components';
import {Grid, HStack, Link, Right, Space, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {object, string} from 'yup';

import {always, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  MyWebDescriptionFormat,
  useGetMyWebExtraLinkQuery,
  useMyWebPublishingOnTenantMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {useBranchId} from '@dms/settings/shared';

import {Nullish} from 'shared/web';

import {PlatformAuthenticationFormData} from './PlatformAuthenticationForm';

export type MyWebAuthenticationFormData = PlatformAuthenticationFormData & {
  descriptionFormat?: MyWebDescriptionFormat;
};

type PlatformAuthenticationFormMyWebProps = {
  defaultValues: PlatformAuthenticationFormData | Nullish;
  defaultDescriptionFormat?: MyWebDescriptionFormat;
  isPublishingOnTenant: boolean;
  onSubmit: FormSubmitHandler<MyWebAuthenticationFormData>;
};

const DESCRIPTION_FORMAT_OPTIONS = [
  {label: i18n.t('page.salesSettings.labels.descriptionFormatPlainText'), value: 'plain_text'},
  {label: i18n.t('page.salesSettings.labels.descriptionFormatMarkdown'), value: 'markdown'},
  {label: i18n.t('page.salesSettings.labels.descriptionFormatHtml'), value: 'html'},
];

export function PlatformAuthenticationFormMyWeb(props: PlatformAuthenticationFormMyWebProps) {
  const branchId = useBranchId();
  const {data: extraLink} = useGetMyWebExtraLinkQuery({
    branchId: isNil(branchId) || props.isPublishingOnTenant ? undefined : branchId,
  });
  const [setPublishOnTenant, {isLoading}] = useMyWebPublishingOnTenantMutation();

  const mode = match([isNil(branchId), props.isPublishingOnTenant])
    .with([false, true], always('Tenant'))
    .with([false, false], always('Branch'))
    .otherwise(always(''));

  const defaultValues: MyWebAuthenticationFormData = {
    ...props.defaultValues,
    username: props.defaultValues?.username ?? '',
    password: props.defaultValues?.password ?? '',
    descriptionFormat: props.defaultDescriptionFormat ?? 'plain_text',
  };

  return (
    <Card title={i18n.t('general.labels.authentication')}>
      <Text size="small" color="secondary">
        {i18n.t('page.settings.labels.authenticationDescription')}
      </Text>
      <Space vertical={4} />
      <Form<MyWebAuthenticationFormData>
        defaultValues={defaultValues}
        schema={schema}
        onSubmit={props.onSubmit}
        key={mode}
        shouldWatchForUnsavedChanges
      >
        {(control) => (
          <VStack spacing={4}>
            {isNotNil(branchId) && (
              <DataStatus isLoading={isLoading}>
                <Switch
                  label={i18n.t('page.settings.labels.publishOnTenant')}
                  value={props.isPublishingOnTenant}
                  onChange={(isEnabled) => setPublishOnTenant({isEnabled, branchId})}
                />
              </DataStatus>
            )}
            <Grid columns={2}>
              <FormField
                control={control}
                name="username"
                type="text"
                label={i18n.t(`page.advertisement.labels.username${mode}`)}
              />
              <FormField
                control={control}
                name="password"
                type="password"
                label={i18n.t(`page.advertisement.labels.password${mode}`)}
              />
            </Grid>
            <Grid columns={2}>
              <FormField
                control={control}
                name="descriptionFormat"
                type="choice"
                label={i18n.t('page.salesSettings.labels.descriptionFormat')}
                tooltip={i18n.t('page.salesSettings.labels.descriptionFormatTooltip')}
                options={DESCRIPTION_FORMAT_OPTIONS}
              />
            </Grid>
            {extraLink && (
              <>
                <HStack spacing={1} align="center">
                  <Text size="xSmall">{i18n.t('general.labels.URL')}</Text>
                  <Link
                    size="small"
                    data-testid={testIds.settings.advertisingPlatforms('externalLink-myweb')}
                    href={extraLink.link}
                    title={i18n.t('page.advertisement.labels.extraLinkText')}
                  />
                </HStack>
              </>
            )}
            <Right>
              <FormButton control={control} type="submit" title={i18n.t('general.actions.save')} />
            </Right>
          </VStack>
        )}
      </Form>
    </Card>
  );
}

const schema = object({
  username: string().nullable().required(),
  password: string().nullable().required(),
  descriptionFormat: string().oneOf(['plain_text', 'markdown', 'html']).default('plain_text'),
});
